import { getBuildings, getArchivedBuildings } from './localDatabase';
import { getKonzernIds } from './localDatabase';
import { getLocalPhotos } from './photoStorage';
import { getAuditLogs } from './auditLog';
import { encryptData } from './encryption';

/**
 * Create a complete backup of all application data
 */
export const createBackup = async (): Promise<string> => {
  try {
    // Collect all data
    const buildings = getBuildings();
    const archivedBuildings = getArchivedBuildings();
    const konzernIds = getKonzernIds();
    const photos = getLocalPhotos();
    const auditLogs = getAuditLogs();
    
    // Get all users (excluding passwords)
    const usersJson = localStorage.getItem('installtrack_users');
    const users = usersJson ? JSON.parse(usersJson).map((user: any) => {
      const { password, ...userWithoutPassword } = user;
      return userWithoutPassword;
    }) : [];
    
    // Create backup object
    const backup = {
      version: '1.0',
      timestamp: new Date().toISOString(),
      data: {
        buildings,
        archivedBuildings,
        konzernIds,
        photos,
        users,
        auditLogs
      }
    };
    
    // Convert to JSON
    const backupJson = JSON.stringify(backup);
    
    // Encrypt the backup
    const encryptedBackup = encryptData(backupJson);
    
    return encryptedBackup;
  } catch (error) {
    console.error('Error creating backup:', error);
    throw new Error('Fehler beim Erstellen des Backups');
  }
};

/**
 * Download backup as a file
 */
export const downloadBackup = async (): Promise<void> => {
  try {
    const backup = await createBackup();
    
    // Create a blob from the backup
    const blob = new Blob([backup], { type: 'application/octet-stream' });
    
    // Create a download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `installtrack_backup_${new Date().toISOString().slice(0, 10)}.itpb`;
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading backup:', error);
    throw new Error('Fehler beim Herunterladen des Backups');
  }
};

/**
 * Schedule automatic backups
 * @param intervalDays Number of days between backups
 */
export const scheduleAutomaticBackups = (intervalDays: number = 7): void => {
  try {
    // Check when the last backup was created
    const lastBackupTime = localStorage.getItem('installtrack_last_backup_time');
    const now = new Date().getTime();
    
    if (lastBackupTime) {
      const timeSinceLastBackup = now - parseInt(lastBackupTime);
      const daysSinceLastBackup = timeSinceLastBackup / (1000 * 60 * 60 * 24);
      
      if (daysSinceLastBackup < intervalDays) {
        // Not time for a backup yet
        return;
      }
    }
    
    // Create and store backup
    createBackup().then(backup => {
      localStorage.setItem('installtrack_auto_backup', backup);
      localStorage.setItem('installtrack_last_backup_time', now.toString());
      console.log('Automatic backup created successfully');
    }).catch(error => {
      console.error('Error creating automatic backup:', error);
    });
  } catch (error) {
    console.error('Error scheduling automatic backup:', error);
  }
};